// EP-135 Tier 1 — Meeting detail. Header status chip + resolutions
// block (nested) + a "Send for certification" admin-only action.
// Tier 1 only flips status to `closed` — chair + secretary e-sign
// arrives in Tier 4 and writes `certified_at`.
//
// EP-135 Tier 2 — Notices block. Lists every served notice with
// audience/delivered/failed/pending counts, plus a per-recipient
// delivery table on each notice (proof of service, D-4). Admin+
// creates new notices (one delivery row per audience member);
// staff+ marks deliveries delivered/failed.
//
// EP-135 Tier 3 — Attendees (snapshot roll + check-in + proxy) +
// Resolutions extension with per-resolution tally view + ballot list
// + "Record vote" admin-only action. ETDA compliance surface: a
// "View audit log" admin+ action shows the immutable §8b export
// (with secret ballots hiding attendee identity at the serializer).
import { useState } from 'react'
import { useParams, useNavigate } from 'react-router'
import { useTranslation } from 'react-i18next'
import {
  ArrowLeft, Plus, Send, FileText, Mail,
  ChevronDown, ChevronRight, CheckCircle2, XCircle, Clock,
  Users, UserCheck, Vote, BarChart3, FileSearch, RotateCw, Lock,
} from 'lucide-react'
import { PageHeader, Breadcrumbs } from '@/components/layout'
import { Card, Button, Badge, Skeleton, EmptyState } from '@/components/ui'
import { useAuth } from '@/hooks/useAuth'
import {
  useMeeting,
  useResolutions,
  useResolution,
  useSendForCertification,
  useNotices,
  useNotice,
  useUpdateNoticeDelivery,
  useAttendees,
  useSnapshotAttendees,
  useUpdateAttendee,
  useVotes,
  useCloseVoting,
  useCarryOverToNextCall,
  useCommitteeMembers,
} from '@/hooks/useGovernance'
import { useNotificationStore } from '@/stores'
import { useApiErrorTranslator } from '@/hooks/useApiErrorTranslator'
import { useDateTimeFormatter } from '@/hooks/useDateFormatter'
import { ResolutionFormModal } from './components/ResolutionFormModal'
import { NoticeFormModal } from './components/NoticeFormModal'
import { AttendeeFormModal } from './components/AttendeeFormModal'
import { BallotFormModal } from './components/BallotFormModal'
import { MinutesPanel } from './components/MinutesPanel'
import type { ResolutionDetail, ResolutionSummary, AttendeeSummary, VoteSummary, VoteChoice, TallySnapshot } from '@/types/governance'

export function MeetingDetailPage() {
  const { t } = useTranslation('governance')
  const { t: tc } = useTranslation('common')
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const meetingId = Number(id)
  const { user } = useAuth()
  const addToast = useNotificationStore((s) => s.addToast)
  const translateError = useApiErrorTranslator('governance')
  const formatDateTime = useDateTimeFormatter()

  const [resolutionModal, setResolutionModal] = useState(false)
  const [noticeModal, setNoticeModal] = useState(false)
  const [attendeeModal, setAttendeeModal] = useState(false)
  const [expandedNoticeId, setExpandedNoticeId] = useState<number | null>(null)
  const [expandedResolutionId, setExpandedResolutionId] = useState<number | null>(null)
  const [ballotResolutionId, setBallotResolutionId] = useState<number | null>(null)

  const { data: meeting, isLoading, error } = useMeeting(meetingId)
  const { data: resolutions } = useResolutions(meetingId)
  const { data: notices } = useNotices(meetingId)
  const { data: attendees } = useAttendees(meetingId)
  const sendForCert = useSendForCertification()
  const snapshotAttendees = useSnapshotAttendees(meetingId)
  const closeVoting = useCloseVoting(meetingId)
  const carryOver = useCarryOverToNextCall(meetingId)
  // EP-135 Tier 4 (§8 Q-5) — the minutes signers are the CURRENT
  // committee chair + secretary, not `meeting.certified_by_*_id`
  // (those are written BY signing, so sourcing them from there made
  // signing impossible until it had already happened). Once a role
  // has signed we pin the stamped id so the panel keeps showing the
  // person who actually signed, even if the seat changes afterwards.
  const { data: committee } = useCommitteeMembers({ active: true })
  const seatHolderId = (role: 'chair' | 'secretary') =>
    committee?.data.find((m) => m.role === role && m.ended_on === null)?.customer_id ?? null
  const chairSignerId = meeting?.certified_by_chair_id ?? seatHolderId('chair')
  const secretarySignerId = meeting?.certified_by_secretary_id ?? seatHolderId('secretary')

  const isAdminOrOwner = user?.role === 'admin' || user?.role === 'owner'
  const isStaffOrAbove = user?.role !== undefined
  const canCreateResolution = isAdminOrOwner
  const canCreateNotice = isAdminOrOwner
  const canSendForCert = isAdminOrOwner
  const canSnapshotAttendees = isAdminOrOwner
  const canRecordVote = isAdminOrOwner
  const canCloseVoting = isAdminOrOwner
  const canCarryOver = isAdminOrOwner
  const canMarkDelivery = isStaffOrAbove

  const handleSendForCertification = async () => {
    if (!meeting) return
    try {
      await sendForCert.mutateAsync(meeting.id)
      addToast({ type: 'success', message: t('meetings.messages.sent_for_certification') })
    } catch (err) {
      addToast({ type: 'error', message: translateError(err) })
    }
  }

  const handleSnapshotAttendees = async () => {
    try {
      const result = await snapshotAttendees.mutateAsync()
      addToast({
        type: 'success',
        message: result.length === 0
          ? t('attendees.messages.snapshot_existing')
          : t('attendees.messages.snapshot_done', { count: result.length }),
      })
    } catch (err) {
      addToast({ type: 'error', message: translateError(err) })
    }
  }

  const handleCloseVoting = async () => {
    if (!meeting) return
    try {
      await closeVoting.mutateAsync()
      addToast({ type: 'success', message: t('meetings.messages.voting_closed') })
    } catch (err) {
      addToast({ type: 'error', message: translateError(err) })
    }
  }

  const handleCarryOver = async () => {
    if (!meeting) return
    // Default call-2 to one week out at the same time of day; the
    // operator can edit before saving in the future, but for now we
    // take a deterministic default (D-13 — call-2 needs a fresh
    // notice window).
    // DAT-1: build the prefill in the browser's own local fields, not
    // toISOString: `new Date(dt)` below parses this prompt value (if left
    // unedited) as LOCAL time, so a UTC-labelled default would read back
    // shifted by the browser's UTC offset instead of "the same time of day".
    const inOneWeek = new Date(Date.now() + 7 * 24 * 60 * 60 * 1000)
    const defaultCallTwo =
      `${inOneWeek.getFullYear()}-${String(inOneWeek.getMonth() + 1).padStart(2, '0')}-` +
      `${String(inOneWeek.getDate()).padStart(2, '0')}T` +
      `${String(inOneWeek.getHours()).padStart(2, '0')}:${String(inOneWeek.getMinutes()).padStart(2, '0')}`
    const dt = window.prompt(t('meetings.messages.carry_over_date_required'), defaultCallTwo)
    if (!dt) return
    const meetingAt = new Date(dt).toISOString()
    try {
      await carryOver.mutateAsync({ meeting_at: meetingAt })
      addToast({ type: 'success', message: t('meetings.messages.carried_over') })
    } catch (err) {
      addToast({ type: 'error', message: translateError(err) })
    }
  }

  if (isLoading) {
    return (
      <div className="space-y-6">
        <Skeleton className="h-12 w-1/2" />
        <Skeleton className="h-32 w-full" />
        <Skeleton className="h-64 w-full" />
      </div>
    )
  }

  if (error || !meeting) {
    return (
      <div className="space-y-6">
        <EmptyState
          title={tc('errors.something_went_wrong')}
          description={tc('errors.something_went_wrong')}
          action={<Button onClick={() => navigate('/meetings')}>{t('meetings.actions.back_to_meetings')}</Button>}
        />
      </div>
    )
  }

  // Mirrors Meeting#ready_for_signing? on the BE (closed | voting_closed),
  // plus in_progress so a meeting can be sent straight to certification.
  const showSendForCert = canSendForCert &&
    ['closed', 'voting_closed', 'in_progress'].includes(meeting.status)
  const showCloseVoting = canCloseVoting && meeting.status === 'in_progress'
  const showCarryOver = canCarryOver && meeting.call_number === 1 &&
    !['closed', 'certified', 'voting_closed'].includes(meeting.status)

  return (
    <>
      <div className="space-y-6">
        <Breadcrumbs
          items={[
            { label: t('meetings.title'), href: '/meetings' },
            { label: meeting.title },
          ]}
        />

        <PageHeader
          title={meeting.title}
          description={formatDateTime(meeting.meeting_at)}
          titleAction={<Badge variant="count">{t(`meetings.statuses.${meeting.status}`)}</Badge>}
          isRefreshing={false}
          overflowActions={[
            ...(showSendForCert
              ? [{
                  key: 'send-for-cert',
                  label: t('meetings.actions.send_for_certification'),
                  icon: Send,
                  onClick: handleSendForCertification,
                  loading: sendForCert.isPending,
                  testId: 'btn-send-for-cert',
                }]
              : []),
            ...(showCloseVoting
              ? [{
                  key: 'close-voting',
                  label: t('meetings.actions.close_voting'),
                  icon: Lock,
                  onClick: handleCloseVoting,
                  loading: closeVoting.isPending,
                  testId: 'btn-close-voting',
                }]
              : []),
            ...(showCarryOver
              ? [{
                  key: 'carry-over',
                  label: t('meetings.actions.carry_over'),
                  icon: RotateCw,
                  onClick: handleCarryOver,
                  loading: carryOver.isPending,
                  testId: 'btn-carry-over',
                }]
              : []),
          ]}
          primaryAction={
            canCreateResolution
              ? {
                  key: 'add-resolution',
                  label: t('meetings.actions.add_resolution'),
                  icon: Plus,
                  onClick: () => setResolutionModal(true),
                  testId: 'btn-add-resolution',
                }
              : undefined
          }
        />

        <Card bodyClassName="space-y-3">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm">
            <div>
              <span className="text-base-content/60">{t('meetings.labels.meeting_type')}:</span>{' '}
              <span className="font-medium">{t(`meetings.types.${meeting.meeting_type}`)}</span>
            </div>
            <div>
              <span className="text-base-content/60">{t('meetings.labels.call_number')}:</span>{' '}
              <span className="font-medium">{meeting.call_number}</span>
            </div>
            {meeting.venue && (
              <div>
                <span className="text-base-content/60">{t('meetings.labels.venue')}:</span>{' '}
                <span className="font-medium">{meeting.venue}</span>
              </div>
            )}
            <div>
              <span className="text-base-content/60">{t('meetings.labels.notice_lead_days')}:</span>{' '}
              <span className="font-medium">{meeting.notice_lead_days}</span>
            </div>
            {meeting.agenda && (
              <div className="md:col-span-2">
                <span className="text-base-content/60">{t('meetings.labels.agenda')}:</span>{' '}
                <span className="font-medium whitespace-pre-wrap">{meeting.agenda}</span>
              </div>
            )}
          </div>
        </Card>

        {/* EP-135 Tier 3 — Attendees (per-meeting roll snapshot). Admin+
            can snapshot the active OwnerContract roll; staff+ can
            check-in attendees and assign proxies. */}
        <Card
          title={t('attendees.title')}
          description={t('attendees.description')}
          bodyClassName="space-y-3"
          actions={
            canSnapshotAttendees ? (
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  leftIcon={<UserCheck className="w-4 h-4" />}
                  onClick={handleSnapshotAttendees}
                  loading={snapshotAttendees.isPending}
                  data-testid="btn-snapshot-attendees"
                >
                  {t('attendees.actions.snapshot')}
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  leftIcon={<Plus className="w-4 h-4" />}
                  onClick={() => setAttendeeModal(true)}
                  data-testid="btn-add-attendee"
                >
                  {t('attendees.actions.add')}
                </Button>
              </div>
            ) : undefined
          }
        >
          {!attendees || attendees.length === 0 ? (
            <EmptyState
              icon={<Users className="w-10 h-10" />}
              title={t('attendees.empty.no_attendees')}
              description={t('attendees.empty.no_attendees_description')}
              action={
                canSnapshotAttendees ? (
                  <Button leftIcon={<UserCheck className="w-4 h-4" />} onClick={handleSnapshotAttendees}>
                    {t('attendees.actions.snapshot')}
                  </Button>
                ) : undefined
              }
            />
          ) : (
            <div className="overflow-x-auto">
              <table className="table">
                <thead>
                  <tr>
                    <th>{t('attendees.labels.customer')}</th>
                    <th>{t('attendees.labels.presence')}</th>
                    <th>{t('attendees.labels.weight')}</th>
                    <th>{t('attendees.labels.units')}</th>
                    <th>{t('attendees.labels.proxy_holder')}</th>
                  </tr>
                </thead>
                <tbody>
                  {attendees.map((a) => (
                    <AttendeeRow
                      key={a.id}
                      meetingId={meetingId}
                      attendee={a}
                      canEdit={isStaffOrAbove}
                    />
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </Card>

        {/* Resolutions block — extended for Tier 3 to include the
            per-resolution tally view + ballot list (§3b frozen
            snapshot) + "Record vote" admin action. Each row expands
            on click to reveal its full detail. */}
        <Card title={t('resolutions.title')} bodyClassName="space-y-3">
          {!resolutions || resolutions.length === 0 ? (
            <EmptyState
              icon={<FileText className="w-10 h-10" />}
              title={t('meetings.empty.no_resolutions')}
              description={t('meetings.empty.no_resolutions_description')}
              action={
                canCreateResolution ? (
                  <Button leftIcon={<Plus className="w-4 h-4" />} onClick={() => setResolutionModal(true)}>
                    {t('meetings.actions.add_resolution')}
                  </Button>
                ) : undefined
              }
            />
          ) : (
            <div className="space-y-2">
              {resolutions.map((r) => (
                <ResolutionCard
                  key={r.id}
                  meetingId={meetingId}
                  resolution={r}
                  isExpanded={expandedResolutionId === r.id}
                  onToggle={() =>
                    setExpandedResolutionId(expandedResolutionId === r.id ? null : r.id)
                  }
                  onRecordVote={() => setBallotResolutionId(r.id)}
                  canRecordVote={canRecordVote && meeting.status === 'in_progress'}
                />
              ))}
              {canCreateResolution && (
                <div className="pt-2">
                  <Button
                    variant="outline"
                    leftIcon={<Plus className="w-4 h-4" />}
                    onClick={() => setResolutionModal(true)}
                  >
                    {t('meetings.actions.add_resolution')}
                  </Button>
                </div>
              )}
            </div>
          )}
        </Card>


        {/* EP-135 Tier 2 — Notices + proof of service. */}
        <Card title={t('notices.title')} bodyClassName="space-y-3">
          {!notices || notices.length === 0 ? (
            <EmptyState
              icon={<Mail className="w-10 h-10" />}
              title={t('meetings.empty.no_notices')}
              description={t('meetings.empty.no_notices_description')}
              action={
                canCreateNotice ? (
                  <Button
                    leftIcon={<Plus className="w-4 h-4" />}
                    onClick={() => setNoticeModal(true)}
                    data-testid="btn-new-notice"
                  >
                    {t('meetings.actions.new_notice')}
                  </Button>
                ) : undefined
              }
            />
          ) : (
            <div className="space-y-2">
              {notices.map((n) => (
                <NoticeRow
                  key={n.id}
                  meetingId={meetingId}
                  noticeId={n.id}
                  sentAt={n.sent_at}
                  audienceCount={n.audience_count}
                  deliveredCount={n.delivered_count}
                  failedCount={n.failed_count}
                  pendingCount={n.pending_count}
                  body={n.body}
                  isExpanded={expandedNoticeId === n.id}
                  onToggle={() =>
                    setExpandedNoticeId(expandedNoticeId === n.id ? null : n.id)
                  }
                  canMarkDelivery={canMarkDelivery}
                />
              ))}
              {canCreateNotice && (
                <div className="pt-2">
                  <Button
                    variant="outline"
                    leftIcon={<Plus className="w-4 h-4" />}
                    onClick={() => setNoticeModal(true)}
                  >
                    {t('meetings.actions.new_notice')}
                  </Button>
                </div>
              )}
            </div>
          )}
        </Card>

        {/* EP-135 Tier 4 — minutes generation + dual-sign e-sign flow
            (§8 Q-5). The MinutesPanel renders the DocumentBuilder
            payload (header + attendance + resolutions + certification)
            and exposes sign / reset affordances for admin+. Only
            mounted once the meeting is closed or voting_closed — the
            DocumentBuilder itself accepts earlier states but the
            signing actions need a tally to certify against. */}
        {(meeting.status === 'closed' ||
          meeting.status === 'voting_closed' ||
          meeting.status === 'certified') && (
          <MinutesPanel
            meetingId={meetingId}
            canSign={isAdminOrOwner}
            chairCustomerId={chairSignerId}
            secretaryCustomerId={secretarySignerId}
          />
        )}

        {/* ETDA ขมธอ. 26-2564 audit log — admin+ read. Links out
            to a dedicated page so the table stays printable; the
            page itself is implemented below as AuditLogPage. */}
        <Card
          title={t('votes.audit_log.title')}
          description={t('votes.audit_log.description')}
          actions={
            <Button
              variant="outline"
              leftIcon={<FileSearch className="w-4 h-4" />}
              onClick={() => navigate(`/meetings/${meetingId}/audit_log`)}
              data-testid="btn-view-audit-log"
            >
              {t('meetings.actions.view_audit_log')}
            </Button>
          }
        />

        <Button variant="ghost" leftIcon={<ArrowLeft className="w-4 h-4" />} onClick={() => navigate('/meetings')}>
          {t('meetings.actions.back_to_meetings')}
        </Button>
      </div>

      <ResolutionFormModal
        isOpen={resolutionModal}
        meetingId={meetingId}
        onClose={() => {
          setResolutionModal(false)
          addToast({ type: 'success', message: t('meetings.messages.resolution_added') })
        }}
      />

      <NoticeFormModal
        isOpen={noticeModal}
        meetingId={meetingId}
        onClose={() => setNoticeModal(false)}
      />

      <AttendeeFormModal
        isOpen={attendeeModal}
        meetingId={meetingId}
        onClose={() => setAttendeeModal(false)}
      />

      {ballotResolutionId !== null && (
        <BallotFormModal
          isOpen
          meetingId={meetingId}
          resolutionId={ballotResolutionId}
          attendees={(attendees || []).map((a) => ({
            id: a.id,
            customer_name: a.customer_name,
          }))}
          onClose={() => setBallotResolutionId(null)}
        />
      )}
    </>
  )
}

// ---------------------------------------------------------------------------
// AttendeeRow — table row + inline presence editor. Staff+ can mark
// the attendee absent / remote / in_person / proxy. The frozen
// weight_snapshot + unit_snapshot are display-only (§3 + §8 — they
// are the audit-of-record at notice time).
// ---------------------------------------------------------------------------
interface AttendeeRowProps {
  meetingId: number
  attendee: AttendeeSummary
  canEdit: boolean
}

function AttendeeRow({ meetingId, attendee, canEdit }: AttendeeRowProps) {
  const { t } = useTranslation('governance')
  const updateAttendee = useUpdateAttendee(meetingId)
  const addToast = useNotificationStore((s) => s.addToast)
  const translateError = useApiErrorTranslator('governance')

  const setPresence = async (presence: 'absent' | 'remote' | 'in_person' | 'proxy') => {
    try {
      await updateAttendee.mutateAsync({ id: attendee.id, presence })
    } catch (err) {
      addToast({ type: 'error', message: translateError(err) })
    }
  }

  const unitLabels = (attendee.unit_snapshot || [])
    .map((u) => `#${u.unit_id} (${u.ratio})`)
    .join(', ')

  return (
    <tr className="hover:bg-base-200" data-testid={`attendee-row-${attendee.id}`}>
      <td className="font-medium">{attendee.customer_name}</td>
      <td>
        <Badge variant="count">{t(`attendees.presences.${attendee.presence}`)}</Badge>
        {canEdit && (
          <div className="flex gap-1 mt-1">
            {(['absent', 'remote', 'in_person', 'proxy'] as const).map((p) => (
              <Button
                key={p}
                variant={attendee.presence === p ? 'primary' : 'ghost'}
                size="sm"
                onClick={() => setPresence(p)}
                loading={updateAttendee.isPending}
                data-testid={`btn-attendee-presence-${attendee.id}-${p}`}
              >
                {t(`attendees.actions.mark_${p === 'in_person' ? 'in_person' : p}`)}
              </Button>
            ))}
          </div>
        )}
      </td>
      <td className="font-mono">{attendee.weight_snapshot}</td>
      <td className="text-xs text-base-content/60">{unitLabels || '—'}</td>
      <td className="text-xs">{attendee.proxy_holder_name || '—'}</td>
    </tr>
  )
}

// ---------------------------------------------------------------------------
// ResolutionCard — collapsible card per resolution. Header shows the
// seq/title/decided_by/status chips; expanded body renders the
// frozen tally (§3b), the ballot list, the "Record vote" admin
// action, and (for secret resolutions) the §4a serializer note.
// ---------------------------------------------------------------------------
interface ResolutionCardProps {
  meetingId: number
  resolution: ResolutionSummary
  isExpanded: boolean
  onToggle: () => void
  onRecordVote: () => void
  canRecordVote: boolean
}

function ResolutionCard({
  meetingId,
  resolution,
  isExpanded,
  onToggle,
  onRecordVote,
  canRecordVote,
}: ResolutionCardProps) {
  const { t } = useTranslation('governance')
  // The summary endpoint doesn't return `tally_snapshot` or `secret`'s
  // weight info; we lazy-load the detail when the operator expands
  // the row so the tally view sees the frozen snapshot (§3b).
  const { data: detail } = useResolution(meetingId, resolution.id)

  return (
    <div className="border border-base-200 rounded-lg overflow-hidden" data-testid={`resolution-card-${resolution.id}`}>
      <button
        type="button"
        onClick={onToggle}
        className="w-full flex items-center justify-between gap-3 px-3 py-3 hover:bg-base-200/50 text-left"
        data-testid={`btn-toggle-resolution-${resolution.id}`}
      >
        <div className="flex items-center gap-2 min-w-0 flex-1">
          {isExpanded ? (
            <ChevronDown className="w-4 h-4 flex-shrink-0" />
          ) : (
            <ChevronRight className="w-4 h-4 flex-shrink-0" />
          )}
          <div className="min-w-0 flex-1">
            <div className="text-sm font-medium">
              #{resolution.seq} {resolution.title}
            </div>
            {resolution.secret && (
              <div className="text-xs text-base-content/60 inline-flex items-center gap-1">
                <Lock className="w-3 h-3" /> {t('resolutions.labels.secret')}
              </div>
            )}
          </div>
        </div>
        <div className="flex items-center gap-2 flex-shrink-0">
          <Badge variant="count">{t(`resolutions.decided_by.${resolution.decided_by}`)}</Badge>
          <Badge variant="count">{t(`resolutions.statuses.${resolution.status}`)}</Badge>
        </div>
      </button>
      {isExpanded && (
        <div className="border-t border-base-200 px-3 py-3 bg-base-100 space-y-3">
          {detail ? (
            <TallyBlock resolution={detail} />
          ) : (
            <Skeleton className="h-16 w-full" />
          )}
          <BallotsBlock
            meetingId={meetingId}
            resolutionId={resolution.id}
            secret={resolution.secret}
          />
          {canRecordVote && (
            <div>
              <Button
                variant="outline"
                size="sm"
                leftIcon={<Vote className="w-4 h-4" />}
                onClick={onRecordVote}
                data-testid={`btn-record-vote-${resolution.id}`}
              >
                {t('votes.actions.record_vote')}
              </Button>
            </div>
          )}
        </div>
      )}
    </div>
  )
}

// ---------------------------------------------------------------------------
// TallyBlock — renders the frozen tally snapshot from
// `resolutions.tally_snapshot` (§3b). If the resolution has not yet
// been close-voted, no snapshot exists; we surface that gap rather
// than a stale or live tally.
// ---------------------------------------------------------------------------
function TallyBlock({ resolution }: { resolution: ResolutionDetail }) {
  const { t } = useTranslation('governance')
  const formatDateTime = useDateTimeFormatter()
  const tally = resolution.tally_snapshot as TallySnapshot | null

  if (!tally) {
    return (
      <Card bodyClassName="text-sm text-base-content/60">
        <div className="flex items-center gap-2">
          <BarChart3 className="w-4 h-4" />
          {t('votes.tally.no_snapshot')}
        </div>
      </Card>
    )
  }

  const cw = tally.choice_weights || {}
  const forWeight = cw.for ?? '0'
  const againstWeight = cw.against ?? '0'
  const abstainWeight = cw.abstain ?? '0'

  return (
    <Card
      title={t('votes.tally.title')}
      bodyClassName="space-y-2"
      actions={
        <Badge variant="count">
          {(tally.total_cast ?? '0')}{t('votes.tally.total_cast')}
        </Badge>
      }
    >
      <div className="grid grid-cols-3 gap-3 text-sm">
        <div>
          <div className="text-base-content/60">{t('votes.tally.for')}</div>
          <div className="font-mono text-lg">{forWeight}</div>
        </div>
        <div>
          <div className="text-base-content/60">{t('votes.tally.against')}</div>
          <div className="font-mono text-lg">{againstWeight}</div>
        </div>
        <div>
          <div className="text-base-content/60">{t('votes.tally.abstain')}</div>
          <div className="font-mono text-lg">{abstainWeight}</div>
        </div>
      </div>
      {tally.snapshot_at && (
        <div className="text-xs text-base-content/60">
          {t('votes.tally.snapshot_at')}: {formatDateTime(tally.snapshot_at)}
        </div>
      )}
    </Card>
  )
}

// ---------------------------------------------------------------------------
// BallotsBlock — per-resolution ballot list (operator side). §4a +
// D-2 — secret resolutions hide attendee_id + customer_name at the
// serializer; the receipt_hash + aggregate choice weight stay
// visible so a voter can claim their receipt.
// ---------------------------------------------------------------------------
interface BallotsBlockProps {
  meetingId: number
  resolutionId: number
  secret: boolean
}

function BallotsBlock({ meetingId, resolutionId, secret }: BallotsBlockProps) {
  const { t } = useTranslation('governance')
  const formatDateTime = useDateTimeFormatter()
  const { data: votes } = useVotes(meetingId, resolutionId)

  if (!votes || votes.length === 0) {
    return (
      <EmptyState
        icon={<Vote className="w-8 h-8" />}
        title={t('votes.empty.no_votes')}
        description={t('votes.empty.no_votes_description')}
      />
    )
  }

  return (
    <div className="overflow-x-auto">
      <table className="table table-sm" data-testid={`ballots-table-${resolutionId}`}>
        <thead>
          <tr>
            <th>{t('votes.labels.attendee')}</th>
            <th>{t('votes.labels.choice')}</th>
            <th>{t('votes.labels.weight')}</th>
            <th>{t('votes.labels.cast_at')}</th>
            <th>{t('votes.labels.cast_via')}</th>
            <th>{t('votes.labels.identity_level')}</th>
            <th>{t('votes.labels.receipt_hash')}</th>
          </tr>
        </thead>
        <tbody>
          {votes.map((v) => (
            <BallotRow
              key={v.id}
              vote={v}
              secret={secret}
              formatDateTime={formatDateTime}
            />
          ))}
        </tbody>
      </table>
      {secret && (
        <div className="text-xs text-base-content/60 mt-2">
          {t('votes.messages.secret_no_identity')}
        </div>
      )}
    </div>
  )
}

function BallotRow({
  vote,
  secret,
  formatDateTime,
}: {
  vote: VoteSummary
  secret: boolean
  formatDateTime: (iso: string) => string
}) {
  const { t } = useTranslation('governance')
  // §4a — even though the API hides attendee_id for secret ballots,
  // we also hide it client-side as a defense-in-depth.
  const showIdentity = !secret && vote.attendee_id !== null

  return (
    <tr className="hover:bg-base-200" data-testid={`ballot-row-${vote.id}`}>
      <td>
        {showIdentity ? vote.customer_name : <Lock className="w-3 h-3 inline" />}
      </td>
      <td>
        <Badge variant="count">{t(`votes.choices.${vote.choice as VoteChoice}`)}</Badge>
      </td>
      <td className="font-mono">{vote.weight_at_cast}</td>
      <td className="text-xs">{formatDateTime(vote.cast_at)}</td>
      <td className="text-xs">{t(`votes.cast_via.${vote.cast_via}`)}</td>
      <td className="text-xs">{t(`votes.identity_levels.${vote.identity_level}`)}</td>
      <td className="font-mono text-xs">{vote.receipt_hash.slice(0, 12)}…</td>
    </tr>
  )
}

// ---------------------------------------------------------------------------
// NoticeRow — kept identical to Tier 2. Moved below the new Tier-3
// sections so the page layout reads attendees → resolutions → notices
// → audit log (the order the chair works through on meeting day).
// ---------------------------------------------------------------------------
interface NoticeRowProps {
  meetingId: number
  noticeId: number
  sentAt: string
  audienceCount: number
  deliveredCount: number
  failedCount: number
  pendingCount: number
  body: string | null
  isExpanded: boolean
  onToggle: () => void
  canMarkDelivery: boolean
}

function NoticeRow({
  meetingId,
  noticeId,
  sentAt,
  audienceCount,
  deliveredCount,
  failedCount,
  pendingCount,
  body,
  isExpanded,
  onToggle,
  canMarkDelivery,
}: NoticeRowProps) {
  const { t } = useTranslation('governance')
  const formatDateTime = useDateTimeFormatter()

  const { data: notice, isLoading } = useNotice(meetingId, noticeId)

  return (
    <div className="border border-base-200 rounded-lg overflow-hidden">
      <button
        type="button"
        onClick={onToggle}
        className="w-full flex items-center justify-between gap-3 px-3 py-3 hover:bg-base-200/50 text-left"
        data-testid={`btn-toggle-notice-${noticeId}`}
      >
        <div className="flex items-center gap-2 min-w-0 flex-1">
          {isExpanded ? (
            <ChevronDown className="w-4 h-4 flex-shrink-0" />
          ) : (
            <ChevronRight className="w-4 h-4 flex-shrink-0" />
          )}
          <div className="min-w-0 flex-1">
            <div className="text-sm font-medium">{formatDateTime(sentAt)}</div>
            {body && (
              <div className="text-xs text-base-content/60 line-clamp-1">{body}</div>
            )}
          </div>
        </div>
        <div className="flex items-center gap-2 flex-shrink-0">
          {deliveredCount > 0 && (
            <Badge variant="count" data-testid={`notice-delivered-${noticeId}`}>
              {t('notices.counts.delivered', { count: deliveredCount })}
            </Badge>
          )}
          {failedCount > 0 && (
            <Badge variant="count" data-testid={`notice-failed-${noticeId}`}>
              {t('notices.counts.failed', { count: failedCount })}
            </Badge>
          )}
          {pendingCount > 0 && (
            <Badge variant="count" data-testid={`notice-pending-${noticeId}`}>
              {t('notices.counts.pending', { count: pendingCount })}
            </Badge>
          )}
          <Badge variant="count">
            {t('notices.counts.audience', { count: audienceCount })}
          </Badge>
        </div>
      </button>
      {isExpanded && (
        <div className="border-t border-base-200 px-3 py-3 bg-base-100">
          {isLoading ? (
            <Skeleton className="h-24 w-full" />
          ) : notice ? (
            <NoticeDeliveriesTable
              meetingId={meetingId}
              noticeId={noticeId}
              deliveries={notice.notice_deliveries}
              canMarkDelivery={canMarkDelivery}
            />
          ) : (
            <p className="text-xs text-base-content/60">
              {t('meetings.empty.could_not_load_notice')}
            </p>
          )}
        </div>
      )}
    </div>
  )
}

function NoticeDeliveriesTable({
  meetingId,
  noticeId,
  deliveries,
  canMarkDelivery,
}: {
  meetingId: number
  noticeId: number
  deliveries: ReturnType<typeof useNotice> extends { data: infer N }
    ? N extends { notice_deliveries: infer D }
      ? D
      : never
    : never
  canMarkDelivery: boolean
}) {
  const { t } = useTranslation('governance')
  const formatDateTime = useDateTimeFormatter()
  const updateDelivery = useUpdateNoticeDelivery(meetingId, noticeId)
  const addToast = useNotificationStore((s) => s.addToast)
  const translateError = useApiErrorTranslator('governance')

  const handleMarkDelivered = async (deliveryId: number) => {
    try {
      await updateDelivery.mutateAsync({
        id: deliveryId,
        delivered_at: new Date().toISOString(),
      })
    } catch (err) {
      addToast({ type: 'error', message: translateError(err) })
    }
  }

  const handleMarkFailed = async (deliveryId: number) => {
    const reason = window.prompt(t('notices.help.failed_reason_prompt'))
    if (!reason || !reason.trim()) return
    try {
      await updateDelivery.mutateAsync({
        id: deliveryId,
        failed_reason: reason.trim(),
      })
    } catch (err) {
      addToast({ type: 'error', message: translateError(err) })
    }
  }

  const handleClear = async (deliveryId: number) => {
    try {
      await updateDelivery.mutateAsync({
        id: deliveryId,
        delivered_at: null,
        failed_reason: null,
      })
    } catch (err) {
      addToast({ type: 'error', message: translateError(err) })
    }
  }

  if (!deliveries || deliveries.length === 0) {
    return (
      <p className="text-xs text-base-content/60">
        {t('meetings.empty.no_deliveries')}
      </p>
    )
  }

  return (
    <div className="overflow-x-auto">
      <table className="table table-sm">
        <thead>
          <tr>
            <th>{t('notices.labels.recipient')}</th>
            <th>{t('notices.labels.channel')}</th>
            <th>{t('notices.labels.status')}</th>
            <th>{t('notices.labels.detail')}</th>
            {canMarkDelivery && <th></th>}
          </tr>
        </thead>
        <tbody>
          {deliveries.map((d) => {
            const isDelivered = d.delivered
            const isFailed = !!d.failed_reason
            const isPending = !isDelivered && !isFailed
            return (
              <tr key={d.id} className="hover:bg-base-200" data-testid={`delivery-row-${d.id}`}>
                <td>
                  <div className="font-medium">{d.customer_name || '—'}</div>
                  {d.customer_phone && (
                    <div className="text-xs text-base-content/60">{d.customer_phone}</div>
                  )}
                </td>
                <td>
                  <Badge variant="count">{t(`notices.channels.${d.channel}`)}</Badge>
                </td>
                <td>
                  {isDelivered && (
                    <span className="inline-flex items-center gap-1 text-success">
                      <CheckCircle2 className="w-4 h-4" />
                      {t('notices.delivery.delivered')}
                    </span>
                  )}
                  {isFailed && (
                    <span className="inline-flex items-center gap-1 text-error">
                      <XCircle className="w-4 h-4" />
                      {t('notices.delivery.failed')}
                    </span>
                  )}
                  {isPending && (
                    <span className="inline-flex items-center gap-1 text-base-content/60">
                      <Clock className="w-4 h-4" />
                      {t('notices.delivery.pending')}
                    </span>
                  )}
                </td>
                <td className="text-xs text-base-content/60">
                  {isDelivered && d.delivered_at && formatDateTime(d.delivered_at)}
                  {isFailed && d.failed_reason}
                </td>
                {canMarkDelivery && (
                  <td className="text-right">
                    <div className="flex justify-end gap-1">
                      {!isDelivered && (
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleMarkDelivered(d.id)}
                          data-testid={`btn-mark-delivered-${d.id}`}
                        >
                          {t('notices.actions.mark_delivered')}
                        </Button>
                      )}
                      {!isFailed && (
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleMarkFailed(d.id)}
                          data-testid={`btn-mark-failed-${d.id}`}
                        >
                          {t('notices.actions.mark_failed')}
                        </Button>
                      )}
                      {(isDelivered || isFailed) && (
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleClear(d.id)}
                          data-testid={`btn-clear-delivery-${d.id}`}
                        >
                          {t('notices.actions.clear')}
                        </Button>
                      )}
                    </div>
                  </td>
                )}
              </tr>
            )
          })}
        </tbody>
      </table>
    </div>
  )
}